import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DataTables from 'material-ui-datatables';
import FontIcon from 'material-ui/FontIcon';
import AppBar from 'material-ui/AppBar';
import IconButton from 'material-ui/IconButton';
import DatePicker from 'material-ui/DatePicker';
import moment from 'moment';
import _ from 'lodash';

import { showDialog, hideDialog, isDialogOpen } from '../../actions/dialogs';
import { fetchJobs, setJob } from '../../actions/jobs';
import Dashboard from '../Dashboard';
import AssignResources from '../AssignResources';
import AssignResourcesToTasks from '../AssignResourcesToTasks';
import { hasPermission } from '../../utils/authenticated';
import { sortStrings, sortDates, getOrderDirection, contains } from '../../utils/helper';
import Loading from '../Common/Loading';

import styles from './styles';

const getTableColumns = (currentDate, editable, onAssignResources) => {
  return _.flatten([
    {
      key: 'reference',
      label: 'Reference',
      render: (reference, job) => reference,
      sortable: true,
      style: styles.referenceCol,
    }, {
      key: 'customer',
      label: 'Customer',
      render: (customer, job) => customer ? customer.name : '',
      sortable: true,
      style: styles.customerCol,
    }, {
      key: 'primaryContact',
      label: 'Primary Contact',
      render: (primaryContact, job) => primaryContact ? primaryContact.name : '',
      sortable: true,
      style: styles.primaryContactCol,
    }, {
      key: 'description',
      label: 'Description',
      render: (description, job) => description,
      style: styles.descriptionCol,
    }, {
      key: 'tasks',
      label: 'Tasks',
      render: (tasks, job) => tasks ? tasks.count : 0,
      sortable: true,
      style: styles.taskCountCol,
    }, {
      key: 'requested',
      label: 'Requested Resources',
      render: (a, job) => {
        const date = (job.dates || {})[currentDate.format('Y-MM-DD')];
        return date ? (date.resources || { requested: 0 }).requested : 0;
      },
      style: styles.resourcesCol,
    }, {
      key: 'allocated',
      label: 'Allocated Resources',
      render: (a, job) => {
        const date = (job.dates || {})[currentDate.format('Y-MM-DD')];
        return date ? (date.resources || { allocated: 0 }).allocated : 0;
      },
      style: styles.resourcesCol,
    }, {
      key: 'action',
      label: '',
      render: (a, job) => {
        return (<span>{ editable ? <IconButton
          iconClassName="material-icons"
          tooltip="Schedule"
          onClick={() => onAssignResources(job)}
        >
          schedule
        </IconButton> : null }
          </span>);
      },
      alignRight: true,
      style: {
        width: 150,
      },
    },
  ]);
};

const getSorter = (sorter = (a, b) => 0, order = 'asc') => {
  return (a, b) => {
    let result = sorter(a, b);

    if (result === 0) {
      result = sortDates(a.start, b.start);

      if (result === 0) {
        result = sortDates(a.end, b.end);

        if (result === 0) {
          result = sortStrings(a.id, b.id);
        }
      }
    }

    return result * getOrderDirection(order);
  };
};

const getFilter = (filter, currentDate) => {
  return (job) => {
    if (!(job.dates || {})[currentDate.format('Y-MM-DD')]) {
      return false;
    }

    if (filter) {
      if (job.customer && contains(job.customer.name, filter)) {
        return true;
      }
      if (job.primaryContact && contains(job.primaryContact.name, filter)) {
        return true;
      }
      if (contains(job.description, filter)) {
        return true;
      }

      return false;
    }

    return true;
  };
};

class Scheduling extends Component {
  static propTypes = {
    fetchJobs: PropTypes.func.isRequired,
    setJob: PropTypes.func.isRequired,
    jobs: PropTypes.array.isRequired,
    loading: PropTypes.bool,
    showDialog: PropTypes.func.isRequired,
    hideDialog: PropTypes.func.isRequired,
    dialogs: PropTypes.object.isRequired,
  };

  state = {
    selectedRows: [],
    page: 1,
    limit: 10,
    sortBy: getSorter(),
    order: 'asc',
    currentDate: moment(),
    data: [],
  };

  componentWillMount() {
    this.props.fetchJobs();

    this.setState({
      editable: hasPermission(['JobCreator', 'Administrator']),
      data: this.props.jobs.filter(getFilter('', this.state.currentDate)).sort(this.state.sortBy),
    });
  }

  componentWillReceiveProps(nextProps) {
    this.setState({
      data: nextProps.jobs.filter(getFilter('', this.state.currentDate)).sort(this.state.sortBy),
    });
  }

  handleFilterValueChange = (filter) => {
    this.updateData({ filter });
  };

  updateData = (state) => {
    const { jobs } = this.props;
    const { page, limit, sortBy, currentDate, filter } = _.assign({}, this.state, state);

    const data = jobs.filter(getFilter(filter ? filter.trim().toLowerCase() : '', currentDate)).sort(sortBy);
    this.setState({
      page: Math.min(page, Math.floor(data.length / limit) + 1),
      data,
      filter,
      currentDate,
      limit,
    });
  };

  handleSortOrderChange = (key, order) => {
    let sortBy = getSorter();
    switch (key) {
      case 'customer':
        sortBy = getSorter((a, b) => sortStrings(a.customer ? a.customer.name : '', b.customer ? b.customer.name : ''), order);
        break;
      case 'primaryContact':
        sortBy = getSorter((a, b) => sortStrings(a.primaryContact ? a.primaryContact.name : '', b.primaryContact ? b.primaryContact.name : ''), order);
        break;
      case 'tasks':
        sortBy = getSorter((a, b) => {
          const aCount = (a && a.tasks && a.tasks.count) ? a.tasks.count : 0;
          const bCount = (b && b.tasks && b.tasks.count) ? b.tasks.count : 0;

          return aCount - bCount;
        }, order);
        break;
      default:
        break;
    }

    this.setState({
      data: this.state.data.sort(sortBy),
      sortBy,
    });
  };

  handlePreviousPageClick = () => {
    this.updateData({
      page: Math.max(1, this.state.page - 1),
    });
  };

  handleNextPageClick = () => {
    const { data, page, limit } = this.state;

    this.updateData({
      page: Math.min(Math.ceil(data.length / limit), page + 1),
    });
  };

  handleRowSizeChange = (a, limit) => {
    this.updateData({
      limit,
      page: 1,
    });
  };

  previousDay = () => {
    const date = this.state.currentDate;
    date.subtract(1, 'days');

    this.updateData({
      currentDate: date,
    });
  };

  nextDay = () => {
    const date = this.state.currentDate;
    date.add(1, 'days');

    this.updateData({
      currentDate: date,
    });
  };

  openDatePicker = () => {
    this.dp.openDialog();
  };

  handleAssignResources = (job) => {
    this.props.setJob(job);
    this.props.showDialog('assignResources', { currentDate: this.state.currentDate });
  };

  handleDateSelected = (x, date) => {
    this.updateData({
      currentDate: moment(date),
    });
  };

  renderNoData() {
    return (<div style={styles.noDataRow}>
      <FontIcon className="material-icons" style={styles.noData}>date_range</FontIcon>
      <div>No jobs are scheduled for this date</div>
    </div>);
  }

  renderTable() {
    const { loading } = this.props;
    const { currentDate, editable, page, limit, data } = this.state;

    if (loading) {
      return (<Loading/>);
    } else if (data.length === 0) {
      return this.renderNoData();
    } else {
      return (<DataTables
        height={'auto'}
        showRowHover
        columns={getTableColumns(currentDate, editable, this.handleAssignResources)}
        data={data.slice((page - 1) * limit, page * limit)}
        onFilterValueChange={this.handleFilterValueChange}
        onSortOrderChange={this.handleSortOrderChange}
        onPreviousPageClick={this.handlePreviousPageClick}
        onNextPageClick={this.handleNextPageClick}
        onRowSizeChange={this.handleRowSizeChange}
        page={page}
        rowSize={limit}
        rowSizeList={[10, 15, 25, 50, 100]}
        count={data.length}
        filterHintText="Filter"
        headerToolbarMode="filter"
        showHeaderToolbar
      />);
    }
  }

  render() {
    const { currentDate } = this.state;

    return (
      <Dashboard title="Scheduling">
        <AppBar
          title={<span
            style={styles.title}
            onClick={this.openDatePicker}>{currentDate.format('ll')}</span>}
          titleStyle={styles.title}
          iconElementLeft={<IconButton
            iconClassName="material-icons"
            tooltip="Previous Day"
            onClick={this.previousDay}
          >
            chevron_left
          </IconButton>}
          iconElementRight={<IconButton
            iconClassName="material-icons"
            tooltip="Next Day"
            onClick={this.nextDay}
          >
            chevron_right
          </IconButton>}
        />
        <DatePicker
          ref={(dp) => this.dp = dp}
          autoOk
          style={styles.datePicker}
          onChange={this.handleDateSelected}/>
        <div style={styles.tableContainer}>
          {this.renderTable()}
        </div>
        { isDialogOpen(this.props.dialogs, 'assignResources') ? <AssignResources /> : null }
        { isDialogOpen(this.props.dialogs, 'assignResourcesToTasks') ? <AssignResourcesToTasks /> : null}
      </Dashboard>
    );
  }
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchJobs,
    setJob,

    showDialog,
    hideDialog,
  }, dispatch);
}

function mapStateToProps(state) {
  return {
    jobs: state.get('jobs').get('jobs').toJS(),
    loading: state.get('jobs').get('loading'),
    dialogs: state.get('dialogs').get('dialogs').toJS(),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Scheduling);



// WEBPACK FOOTER //
// ./src/components/Scheduling/index.js